import { Field, Formik, FormikProps } from "formik";
import React from "react";
import * as Yup from "yup";
import {CURRENT_USER} from "../../constants";
import { getUser, getVisitorId, getContactInfoFormInitialValues, scrollToDomSelector } from "../../helpers/helperMethods";
import { ContactInfoFormFields } from "../../models/forms.model";
import { contactInfoFormSchema } from "../../validationSchemas/contactInfoFormSchema";
import { FormField } from "../FormFields/FormField";
import { PHONE_NUMBER_MASK } from "../Tabs/PersonalInfo";
import MaskedInput from 'react-text-mask'
import { guestContactInfoFormSchema } from "../../validationSchemas/guestContactInfoFormSchema";
import { customerBusinessService } from "../../services";


interface IContactInfoForm {
  closeModal?: (boolean) => void;
  userId?: number;
  currentGuestUserInfo?: any;
  isGuest?: boolean
  handleSubmitAsGuest?: () => void;
  inline?: boolean;
  formRef?: React.Ref<FormikProps<ContactInfoFormFields>>;
  /** Saved customer email/phone — skip async validate when unchanged. */
  knownEmail?: string | null;
  knownPhone?: string | null;
}

export const normalizeContactPhoneNumber = (
  phone?: string | number | null
): string => {
  // Guest API / stored user can return phone as a number — coerce before replace.
  const digits = String(phone ?? '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  return digits;
};

export const saveContactInfo = (values: ContactInfoFormFields) => {
  const user = getUser();
  const normalizedPhone = normalizeContactPhoneNumber(values.phone);
  user.full_name = values.first_name.concat(' ', values.last_name);
  localStorage.setItem(
    CURRENT_USER,
    JSON.stringify({
      ...user,
      visitor_id: getVisitorId(),
      isInfo: true,
      ...values,
      phone: normalizedPhone,
    })
  );
};

export const validateContactPhoneNumber = async (
  phone: string,
  knownPhone?: string | null
) => {
  const normalizedPhone = normalizeContactPhoneNumber(phone);
  const normalizedKnown = normalizeContactPhoneNumber(knownPhone);
  // Unchanged from the customer's saved phone — skip the validate-number call.
  if (normalizedKnown && normalizedPhone === normalizedKnown) {
    return { valid: true };
  }
  try {
    const res = await customerBusinessService.validatePhoneNo(normalizedPhone);
    if (!res?.data?.data?.valid) {
      return {
        valid: false,
        message: "Phone number is Invalid",
      };
    }
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      message: 'Phone number is Invalid',
    };
  }
};

export const validateContactEmail = async (
  email: string,
  knownEmail?: string | null
) => {
  const normalizedEmail = (email ?? '').trim();
  const normalizedKnown = (knownEmail ?? '').trim();
  // Unchanged from the customer's saved email — skip the validate-email call.
  if (
    normalizedKnown &&
    normalizedEmail.toLowerCase() === normalizedKnown.toLowerCase()
  ) {
    return { valid: true };
  }
  try {
    const res = await customerBusinessService.validateEmail(normalizedEmail);
    const data = res?.data?.data;
    // Block when the API says so (should_block), same gate as phone.valid === false.
    if (data?.should_block || data?.result === 'Invalid') {
      return {
        valid: false,
        message: "Email address is invalid",
      };
    }
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      message: 'Email address is invalid',
    };
  }
};

export type KnownCustomerContact = {
  email?: string | null;
  phone?: string | null;
};

/** Values that failed email/phone API checks — survive Yup wiping form.errors. */
export type ContactApiFailures = {
  email?: string;
  phone?: string;
};

type ContactFormStatus = {
  apiFailures?: ContactApiFailures;
};

const getApiFailures = (
  form: FormikProps<ContactInfoFormFields>
): ContactApiFailures =>
  (form.status as ContactFormStatus | undefined)?.apiFailures || {};

const guestContactFieldTouched = {
  first_name: true,
  last_name: true,
  email: true,
  phone: true,
};

const guestContactFieldSelectors: Record<keyof ContactInfoFormFields, string> = {
  first_name: '[data-testid="customer-first-name-input"]',
  last_name: '[data-testid="customer-last-name-input"]',
  email: '[data-testid="customer-email-input"]',
  phone: '#phone',
};

const guestContactFieldOrder: (keyof ContactInfoFormFields)[] = [
  'first_name',
  'last_name',
  'email',
  'phone',
];

export const scrollToFirstGuestContactFieldError = (
  errors: Partial<Record<keyof ContactInfoFormFields, string>>
) => {
  const firstInvalidField = guestContactFieldOrder.find((field) => errors[field]);
  if (!firstInvalidField) {
    return;
  }
  setTimeout(() => {
    scrollToDomSelector(guestContactFieldSelectors[firstInvalidField]);
  }, 50);
};

export const validateGuestContactFormSync = (
  form: FormikProps<ContactInfoFormFields> | null
): boolean => {
  if (!form) {
    return false;
  }

  try {
    contactInfoFormSchema.validateSync(form.values, { abortEarly: false });
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      const errors: Record<string, string> = {};
      // Keep the first error per field (same as Formik) so required wins over format.
      error.inner.forEach((err) => {
        if (err.path && !errors[err.path]) {
          errors[err.path] = err.message;
        }
      });
      form.setErrors(errors);
      form.setTouched(guestContactFieldTouched, false);
      scrollToFirstGuestContactFieldError(errors);
    } else {
      form.setTouched(guestContactFieldTouched, false);
    }
    return false;
  }

  // Block while any field still has an error, or an unchanged API-failed value.
  const stickyErrors: Partial<Record<keyof ContactInfoFormFields, string>> = {};
  (['first_name', 'last_name', 'email', 'phone'] as const).forEach((field) => {
    if (form.errors[field]) {
      stickyErrors[field] = form.errors[field] as string;
    }
  });

  const failures = getApiFailures(form);
  const emailValue = String(form.values.email ?? '').trim();
  const phoneValue = normalizeContactPhoneNumber(form.values.phone);
  if (failures.email != null && failures.email === emailValue) {
    const message = stickyErrors.email || 'Email address is invalid';
    stickyErrors.email = message;
    form.setFieldError('email', message);
  }
  if (failures.phone != null && failures.phone === phoneValue) {
    const message = stickyErrors.phone || 'Phone number is Invalid';
    stickyErrors.phone = message;
    form.setFieldError('phone', message);
  }

  if (Object.keys(stickyErrors).length > 0) {
    form.setTouched(guestContactFieldTouched, false);
    scrollToFirstGuestContactFieldError(stickyErrors);
    return false;
  }

  form.setTouched(guestContactFieldTouched, false);
  return true;
};

export const validateGuestContactFormAsync = async (
  form: FormikProps<ContactInfoFormFields> | null,
  knownContact?: KnownCustomerContact | null
): Promise<{ valid: boolean; message?: string }> => {
  if (!form) {
    return { valid: false };
  }

  const failures = getApiFailures(form);
  const emailValue = String(form.values.email ?? '').trim();
  const phoneValue = normalizeContactPhoneNumber(form.values.phone);
  // Don't known-skip a value that already failed API and hasn't been edited.
  const knownEmail =
    failures.email != null && failures.email === emailValue
      ? null
      : knownContact?.email;
  const knownPhone =
    failures.phone != null && failures.phone === phoneValue
      ? null
      : knownContact?.phone;

  // Run email + phone validation in parallel to avoid doubling place-order wait.
  const [emailValidation, phoneValidation] = await Promise.all([
    validateContactEmail(form.values.email, knownEmail),
    validateContactPhoneNumber(form.values.phone, knownPhone),
  ]);

  const fieldErrors: Partial<Record<keyof ContactInfoFormFields, string>> = {};
  if (!emailValidation.valid) {
    fieldErrors.email = emailValidation.message;
    form.setFieldError('email', emailValidation.message);
    form.setFieldTouched('email', true, false);
  }
  if (!phoneValidation.valid) {
    fieldErrors.phone = phoneValidation.message;
    form.setFieldError('phone', phoneValidation.message);
    form.setFieldTouched('phone', true, false);
  }

  form.setStatus({
    ...(form.status || {}),
    apiFailures: {
      email: emailValidation.valid ? undefined : emailValue,
      phone: phoneValidation.valid ? undefined : phoneValue,
    },
  });

  if (Object.keys(fieldErrors).length > 0) {
    scrollToFirstGuestContactFieldError(fieldErrors);
    return {
      valid: false,
      message: fieldErrors.email ?? fieldErrors.phone,
    };
  }

  // Clear any prior API errors once email/phone pass.
  if (form.errors.email) {
    form.setFieldError('email', undefined);
  }
  if (form.errors.phone) {
    form.setFieldError('phone', undefined);
  }

  // Guest checkout contact is kept in local form state only (API-driven, never
  // persisted to localStorage/redux); the caller reads the values in-memory.
  return { valid: true };
};

const ContactInfoForm: React.FC<IContactInfoForm> = ({
  closeModal,
  userId,
  currentGuestUserInfo,
  isGuest,
  handleSubmitAsGuest,
  inline = false,
  formRef,
  knownEmail,
  knownPhone,
}) => {
  const initialFormState: ContactInfoFormFields = getContactInfoFormInitialValues(
    currentGuestUserInfo || {}
  );

  const handleFormSubmission = async (
    values: ContactInfoFormFields,
    { setSubmitting, setFieldError }: any
  ) => {
    const [emailValidation, phoneValidation] = await Promise.all([
      values.email
        ? validateContactEmail(values.email, knownEmail)
        : Promise.resolve<{ valid: boolean; message?: string }>({ valid: true }),
      values.phone
        ? validateContactPhoneNumber(values.phone, knownPhone)
        : Promise.resolve<{ valid: boolean; message?: string }>({ valid: true }),
    ]);

    let hasError = false;
    if (!emailValidation.valid) {
      setFieldError('email', emailValidation.message);
      hasError = true;
    }
    if (!phoneValidation.valid) {
      setFieldError('phone', phoneValidation.message);
      hasError = true;
    }
    if (hasError) {
      setSubmitting(false);
      return;
    }

    saveContactInfo(values);
    closeModal?.(true);
    isGuest && handleSubmitAsGuest?.();
  };

  const validationSchema = inline || initialFormState.phone
    ? contactInfoFormSchema
    : guestContactInfoFormSchema;

  const formClassName = inline
    ? "new_form_design profile-info-read-only-field"
    : "new_form_design w-75 my-0 mx-auto delivery_location_select full_width_mob full-width-ipad-modal";

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialFormState}
      enableReinitialize={inline}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmission}
    >
      {({
        values,
        errors,
        touched,
        status,
        setFieldValue,
        setFieldTouched,
        setStatus,
        validateField,
        handleSubmit,
        isSubmitting,
        submitForm,
      }) => (
        <form className={formClassName} onSubmit={handleSubmit}>
          <FormField
            labelText={"First Name"}
            placeholder="Enter your first name"
            type={"text"}
            name={"first_name"}
            errors={errors}
            touched={touched}
            onChange={(e) => {
              const value = e.target.value;
              setFieldValue('first_name', value, false).then(() => {
                validateField('first_name');
              });
            }}
            value={values.first_name}
            dataTestId="customer-first-name-input"
          />
          <FormField
            labelText={"Last Name"}
            placeholder="Enter your last name"
            type={"text"}
            name={"last_name"}
            errors={errors}
            touched={touched}
            onChange={(e) => {
              const value = e.target.value;
              setFieldValue('last_name', value, false).then(() => {
                validateField('last_name');
              });
            }}
            value={values.last_name}
            dataTestId="customer-last-name-input"
          />
          <FormField
            labelText={"Email Address"}
            placeholder="Enter your email address"
            type={"email"}
            name={"email"}
            errors={errors}
            touched={touched}
            onChange={(e) => {
              const value = e.target.value;
              const apiFailures = (status as ContactFormStatus | undefined)
                ?.apiFailures;
              if (apiFailures?.email != null) {
                setStatus({
                  ...(status || {}),
                  apiFailures: { ...apiFailures, email: undefined },
                });
              }
              setFieldValue('email', value, false).then(() => {
                validateField('email');
              });
            }}
            value={values.email}
            dataTestId="customer-email-input"
          />
          {(inline || initialFormState.phone) &&
            <div>
              <label htmlFor="phone">
                Phone Number
              </label>
              <Field
                  name="phone"
                  render={({ field }) => (
                      <MaskedInput
                          {...field}
                          mask={PHONE_NUMBER_MASK}
                          id="phone"
                          placeholder="(223) 123-1234"
                          type="text"
                          value={values.phone}
                          onBlur={() => {
                            setFieldTouched('phone', true, false);
                            validateField('phone');
                          }}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[() -]/g, '');
                            const apiFailures = (
                              status as ContactFormStatus | undefined
                            )?.apiFailures;
                            if (apiFailures?.phone != null) {
                              setStatus({
                                ...(status || {}),
                                apiFailures: { ...apiFailures, phone: undefined },
                              });
                            }
                            setFieldValue('phone', value, false).then(() => {
                              validateField('phone');
                            });
                          }}
                          guide={true}
                          className={`
                          ${errors.phone && touched.phone
                              ? "input_error"
                              : "text-input"}
                            form-control`}
                      />
                  )}
              />
              {errors.phone && touched.phone && (
                  <div className="input-feedback clr-dark-red f-s14 font-rale mt-1 d-inline-block">{errors.phone}</div>
              )}
            </div>
          }
          {!inline && (
            <div className="d-flex justify-content-center">
              <button
                type="submit"
                className="btn btn-large py-2 f-s16 mb-4 mt-3 text-uppercase"
                disabled={isSubmitting}
                onClick={submitForm}
                data-testid="update-contact-info-on-checkout"
              >
                { isGuest ? "PROCEED AS GUEST" : userId ? "CONFIRM CHANGES" : "CONFIRM CONTACT INFO"}
              </button>
            </div>
          )}
        </form>
      )}
    </Formik>
  );
};

export default ContactInfoForm;